import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import { listProperties } from '~/services/AccommodationService';

type AccommodationPropertiesQuery = Parameters<typeof listProperties>[0];

export const accommodationPropertiesQueryKeys = {
  all: ['accommodation', 'properties'] as const,
  lists: () => [...accommodationPropertiesQueryKeys.all, 'list'] as const,
  list: (filters: AccommodationPropertiesQuery) => [...accommodationPropertiesQueryKeys.lists(), filters] as const,
};

export function useGetAccommodationProperties(params: AccommodationPropertiesQuery) {
  return useQuery({
    queryKey: accommodationPropertiesQueryKeys.list(params),
    queryFn: async () => {
      const response = await listProperties(params);
      return response.result;
    },
    // Keep the previous page's rows and total while the next page loads. If `data` dropped to
    // undefined, the grid would receive rowCount 0, treat the current page as out of range and
    // fire onPaginationModelChange({ page: 0 }), resetting the URL page param.
    placeholderData: keepPreviousData,
  });
}

export function useGetAccommodationPropertiesByIds(ids: Array<string>) {
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: accommodationPropertiesQueryKeys.list({ id, limit: '1', page: '1' }),
      queryFn: async () => {
        const response = await listProperties({ id, limit: '1', page: '1' });
        return new Map([[id, response.result.properties[0]]]);
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const propertiesById = new Map(queries.flatMap((query) => (query.data ? Array.from(query.data) : [])));
  return { propertiesById, isLoading: queries.some((query) => query.isLoading) };
}
